import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, ExternalLink, NotebookPen, Plus, Copy, User, Bot, FilePlus2, Boxes, Wand2 } from "lucide-react";
import api from "@/lib/api";
import { streamResearch } from "@/lib/aiStream";
import { safeUrl } from "@/lib/safeUrl";
import { PROVIDER_META, PROVIDER_ORDER } from "./providerMeta";
import { ResearchExtractDialog } from "./ResearchExtractDialog";
import { SaveResearchNoteDialog } from "./SaveResearchNoteDialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const ResearchPanel = ({ open, onOpenChange, contextLabel, onSaveToNotes, onInsertEntity, onCreateEntity, onFillFields, enableExtract, enableSaveNote, onExtracted, presetPrompt }) => {
  const [keys, setKeys] = useState([]);
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [extractState, setExtractState] = useState(null); // { text, sources }
  const [saveNoteState, setSaveNoteState] = useState(null); // { text, sources }
  const scrollRef = useRef(null);

  const connected = keys.filter((k) => k.connected);
  const providerRow = keys.find((k) => k.provider === provider);

  useEffect(() => {
    if (!open) return;
    api.get("/ai/keys").then((res) => {
      setKeys(res.data);
      const conn = res.data.filter((k) => k.connected);
      const pref = PROVIDER_ORDER.find((p) => conn.some((c) => c.provider === p)) || conn[0]?.provider || "";
      setProvider(pref);
    }).catch(() => {});
    setMessages([]);
    if (presetPrompt) setInput(presetPrompt);
  }, [open, presetPrompt]);

  useEffect(() => {
    if (providerRow) setModel(providerRow.default_model);
  }, [providerRow]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming || !provider) return;
    const now = Date.now();
    const history = [...messages, { id: `u-${now}`, role: "user", content: text }];
    setMessages([...history, { id: `a-${now}`, role: "assistant", content: "", sources: [] }]);
    setInput("");
    setStreaming(true);
    const wire = history.map((m) => ({ role: m.role, content: m.content }));
    await streamResearch({
      provider,
      model,
      messages: wire,
      onDelta: (t) => setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + t };
        return copy;
      }),
      onSources: (s) => setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], sources: s };
        return copy;
      }),
      onError: (msg) => { toast.error(msg); setStreaming(false); },
      onDone: () => setStreaming(false),
    });
    setStreaming(false);
  }, [input, streaming, provider, model, messages]);

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0" data-testid="research-panel">
        <SheetHeader className="p-5 pb-3 border-b border-border">
          <SheetTitle className="font-serif text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Research with AI
          </SheetTitle>
          <SheetDescription>
            {contextLabel ? <>Researching for <b className="text-foreground">{contextLabel}</b>. </> : null}
            Answers use your own AI key. Verify against sources before adding anything.
          </SheetDescription>
          {connected.length > 0 && (
            <div className="flex items-center gap-2 pt-2">
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="h-8 w-[150px] text-sm" data-testid="research-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {connected.map((k) => (
                    <SelectItem key={k.provider} value={k.provider}>{PROVIDER_META[k.provider].name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {providerRow && (
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger className="h-8 w-[190px] text-sm" data-testid="research-model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {providerRow.models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </SheetHeader>

        {connected.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-3" data-testid="research-no-key">
            <Sparkles className="w-8 h-8 text-muted-foreground/50" />
            <p className="text-muted-foreground max-w-xs">Connect an AI key to start researching. Perplexity is best for cited sources.</p>
            <Button asChild className="bg-primary hover:bg-primary/90">
              <Link to="/ai-keys" data-testid="research-connect-cta">Connect a provider</Link>
            </Button>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="research-messages">
              {messages.length === 0 && (
                <p className="text-sm text-muted-foreground text-center mt-8">
                  Ask a research question — e.g. “Summarise the Advaita view of Ātman with sources.”
                </p>
              )}
              {messages.map((m, i) => (
                <div key={m.id || i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`research-msg-${m.role}-${i}`}>
                  <div className={`max-w-[92%] rounded-lg px-3.5 py-2.5 text-sm ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-foreground"
                  }`}>
                    <div className="flex items-center gap-1.5 mb-1 opacity-70 text-[11px] font-semibold uppercase tracking-wide">
                      {m.role === "user" ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                      {m.role === "user" ? "You" : PROVIDER_META[provider]?.name || "AI"}
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : "")}
                    </div>
                    {m.sources && m.sources.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border/50 space-y-1" data-testid={`research-sources-${i}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-wide opacity-60">Sources</div>
                        {m.sources.map((s, si) => {
                          const href = safeUrl(s.url);
                          return href ? (
                            <a key={href || `src-${si}`} href={href} target="_blank" rel="noreferrer noopener"
                              className="flex items-center gap-1 text-xs text-primary hover:underline">
                              <ExternalLink className="w-3 h-3 shrink-0" />
                              <span className="truncate">{s.title || href}</span>
                            </a>
                          ) : null;
                        })}
                      </div>
                    )}
                    {m.role === "assistant" && m.content && !streaming && (
                      <div className="flex items-center gap-1 flex-wrap mt-2 pt-2 border-t border-border/50">
                        <Button variant="ghost" size="sm" className="h-7 text-xs"
                          onClick={() => { navigator.clipboard?.writeText(m.content); toast.success("Copied"); }}
                          data-testid={`research-copy-${i}`}>
                          <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                        </Button>
                        {onSaveToNotes && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
                            onClick={() => onSaveToNotes(m.content, m.sources || [])}
                            data-testid={`research-save-notes-${i}`}>
                            <NotebookPen className="w-3.5 h-3.5 mr-1" /> Save to notes
                          </Button>
                        )}
                        {onInsertEntity && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
                            onClick={() => onInsertEntity(m.content, m.sources || [])}
                            data-testid={`research-insert-entity-${i}`}>
                            <FilePlus2 className="w-3.5 h-3.5 mr-1" /> Insert into entity
                          </Button>
                        )}
                        {onFillFields && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
                            onClick={() => onFillFields(m.content, m.sources || [])}
                            data-testid={`research-fill-fields-${i}`}>
                            <Wand2 className="w-3.5 h-3.5 mr-1" /> Auto-fill fields
                          </Button>
                        )}
                        {onCreateEntity && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
                            onClick={() => onCreateEntity(m.content, m.sources || [])}
                            data-testid={`research-create-entity-${i}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Create entity
                          </Button>
                        )}
                        {enableExtract && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
                            onClick={() => { setExtractState({ text: m.content, sources: m.sources || [] }); onOpenChange(false); }}
                            data-testid={`research-extract-entities-${i}`}>
                            <Boxes className="w-3.5 h-3.5 mr-1" /> Extract entities
                          </Button>
                        )}
                        {enableSaveNote && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
                            onClick={() => { setSaveNoteState({ text: m.content, sources: m.sources || [] }); onOpenChange(false); }}
                            data-testid={`research-save-note-${i}`}>
                            <NotebookPen className="w-3.5 h-3.5 mr-1" /> Save note
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-border">
              <div className="flex items-end gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask a research question…"
                  className="min-h-[44px] max-h-32 text-sm"
                  data-testid="research-input"
                />
                <Button onClick={send} disabled={streaming || !input.trim()}
                  className="bg-primary hover:bg-primary/90 shrink-0 h-11 w-11 p-0" data-testid="research-send">
                  {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>

    {extractState && (
      <ResearchExtractDialog
        open={!!extractState}
        onOpenChange={(o) => !o && setExtractState(null)}
        text={extractState.text}
        sources={extractState.sources}
        provider={provider}
        onCommitted={() => { setExtractState(null); onExtracted?.(); }}
      />
    )}
    {saveNoteState && (
      <SaveResearchNoteDialog
        open={!!saveNoteState}
        onOpenChange={(o) => !o && setSaveNoteState(null)}
        text={saveNoteState.text}
        sources={saveNoteState.sources}
      />
    )}
    </>
  );
};
